// GNB 메뉴 Pinia 전역 상태 (code→route·게스트 메뉴는 `@/lib/constants/gnbMenuMap`)

import { defineStore } from 'pinia';
import { datadogRum } from '@datadog/browser-rum';
import axios from '@/utils/axios';
import { useAuthStore } from '@/stores/auth';
import { CODE_TO_ROUTE, STATIC_GUEST_MENU } from '@/lib/constants/gnbMenuMap';
import type { Menu } from '@/lib/constants/gnbMenuMap';

/** GET /gnb-menu `menu` 배열 → 화면용 `Menu[]` */
function mapApiMenu(apiMenu: api.gnb.menu.item[]): Menu[] {
  const outboundQuickFallback = '/outbound/quick';

  return apiMenu.map((m, index) => {
    const children: Menu[] = (m.children ?? []).map((c) => ({
      title: c.title,
      link: CODE_TO_ROUTE[c.code] ?? '/',
    }));
    let link = m.defaultRouter ? CODE_TO_ROUTE[m.defaultRouter] ?? children[0]?.link ?? '/' : children[0]?.link ?? '/';

    // 주문접수(첫 상위 메뉴): API에 하위(children)가 없으면 퀵 접수로 보냄
    if (index === 0 && children.length === 0 && link === '/') {
      link = outboundQuickFallback;
    }

    return { title: m.title, link, children };
  });
}

/** 동시에 여러 곳에서 loadGnbMenu 호출 시 실제 네트워크는 1회만 */
let inflight: Promise<Menu[]> | null = null;

interface GnbState {
  /** 로그인 후 GET /gnb-menu 응답을 매핑한 전체 메뉴 트리(세션 정본; 비로그인 시 비움) */
  gnbMenu: Menu[];
  /** API 기준 첫 상위 메뉴(주로 주문접수)의 진입 링크 — `gnbMenu` 갱신 시 동기화 */
  firstOutboundLink: string;
  /** resetGnbMenu(로그아웃) 이후 in-flight 응답을 state에 반영하지 않기 위한 가드 */
  shouldApplyResult: boolean;
  /** `_fetchAndApplyMenu` / `loadGnbMenu` 진행 중 */
  loading: boolean;
  /** 직전 fetch 실패 메시지(성공·스킵 시 null) */
  lastFetchError: string | null;
}

function defaultFirstOutboundLink(): string {
  return STATIC_GUEST_MENU[0]?.link ?? '/outbound/quick';
}

export const useGnbStore = defineStore('gnb', {
  state: (): GnbState => ({
    gnbMenu: [],
    firstOutboundLink: defaultFirstOutboundLink(),
    shouldApplyResult: true,
    loading: false,
    lastFetchError: null,
  }),

  getters: {
    getMenu(): Menu[] {
      const auth = useAuthStore();
      if (!auth.sessionToken) return STATIC_GUEST_MENU;
      return this.gnbMenu.length > 0 ? this.gnbMenu : STATIC_GUEST_MENU;
    },

    /**
     * 라우팅용 첫 주문접수 URL. `firstOutboundLink`가 비어 있을 때 `/outbound/quick`.
     */
    firstOutboundHref(): string {
      return this.firstOutboundLink || '/outbound/quick';
    },
  },

  /**
   * 액션 네이밍: `_`로 시작하는 메서드는 이 스토어 **내부**(`loadGnbMenu` / `resetGnbMenu` 파이프라인)에서만 호출한다.
   * Pinia 특성상 스토어 인스턴스에 그대로 노출되지만, 컴포넌트·다른 스토어·유틸에서 **직접 호출하지 말 것**
   */
  actions: {
    _syncFirstOutboundFromMenu(fallback: string = '/outbound/quick') {
      this.firstOutboundLink = this.gnbMenu[0]?.link ?? STATIC_GUEST_MENU[0]?.link ?? fallback;
    },

    async _fetchMenuFromApi(): Promise<api.gnb.menu.response> {
      const { data } = await axios.get<api.gnb.menu.response>('/gnb-menu');
      if (!data.success) throw data;
      return data;
    },

    _setMenuFromApiResponse(data: api.gnb.menu.response) {
      if (!this.shouldApplyResult) return;
      const mapped = mapApiMenu(data.menu ?? []);
      if (mapped.length > 0) {
        this.gnbMenu = mapped;
        this._syncFirstOutboundFromMenu();
        this.lastFetchError = null;
      } else {
        datadogRum.addAction('gnb_menu_empty_response', {
          responseCode: data?.code,
          rawMenuLength: data?.menu?.length ?? 0,
        });
      }
    },

    async _fetchAndApplyMenu(): Promise<void> {
      this.loading = true;
      this.lastFetchError = null;
      try {
        const data = await this._fetchMenuFromApi();
        this._setMenuFromApiResponse(data);
      } catch (e: any) {
        this.lastFetchError = e?.message ?? String(e);
        datadogRum.addAction('gnb_menu_fetch_failed', {
          message: e?.message,
          status: e?.response?.status,
          errorCode: e?.response?.data?.code,
        });
      } finally {
        this.loading = false;
      }
    },

    async loadGnbMenu(): Promise<Menu[]> {
      if (!useAuthStore().sessionToken) {
        datadogRum.addAction('gnb_menu_fetch_skipped_no_token', {});
        return this.gnbMenu;
      }
      if (inflight) return inflight;

      this.shouldApplyResult = true;
      const pending = this._fetchAndApplyMenu().then(() => this.gnbMenu);
      inflight = pending;
      pending.finally(() => {
        if (inflight === pending) inflight = null;
      });
      return pending;
    },

    resetGnbMenu() {
      this.gnbMenu = [];
      this.firstOutboundLink = defaultFirstOutboundLink();
      this.shouldApplyResult = false;
      this.loading = false;
      this.lastFetchError = null;
      /** dedupe 해제 — 재로그인 시 `if (inflight) return`에 묶이지 않음. 완료 쪽은 `shouldApplyResult`로 무시 */
      inflight = null;
    },
  },
});
